import React, { useEffect } from "react";
import { useTheme, ThemeName } from "../../util/themes";
import { getConfiguredThemeName } from "../../util/themes/ThemeProvider";

interface ThemeWrapperProps {
  children: React.ReactNode;
}

const LIQUID_GLASS_GLOBAL_CSS = `
  *, *::before, *::after {
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', system-ui, sans-serif !important;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html, body {
    background-color: #F5EDE0;
    color: #0A0A0A;
    min-height: 100vh;
  }

  a {
    color: #0A0A0A;
    text-decoration: none;
    transition: opacity 0.2s ease;
  }
  a:hover { opacity: 0.65; }

  /* MUI overrides para glass — warm cream tokens */
  .MuiAppBar-root {
    background: rgba(245, 237, 224, 0.88) !important;
    backdrop-filter: blur(36px) saturate(200%) !important;
    -webkit-backdrop-filter: blur(36px) saturate(200%) !important;
    border-bottom: 1px solid rgba(255,255,255,0.55) !important;
    box-shadow: none !important;
    color: #0A0A0A !important;
  }

  .MuiToolbar-root {
    background: transparent !important;
    color: #0A0A0A !important;
  }

  .MuiPaper-root {
    background: rgba(255, 250, 240, 0.55);
    backdrop-filter: blur(28px) saturate(180%);
    -webkit-backdrop-filter: blur(28px) saturate(180%);
    border: 1px solid rgba(255,255,255,0.55);
    box-shadow: 0 12px 40px -12px rgba(60,40,20,0.18), inset 0 1px 0 rgba(255,255,255,0.85);
    color: #0A0A0A;
  }

  .MuiCard-root {
    background: rgba(255, 250, 240, 0.55);
    backdrop-filter: blur(28px) saturate(180%);
    -webkit-backdrop-filter: blur(28px) saturate(180%);
    border: 1px solid rgba(255,255,255,0.55);
    box-shadow: 0 12px 40px -12px rgba(60,40,20,0.18), inset 0 1px 0 rgba(255,255,255,0.85);
    border-radius: 24px !important;
    color: #0A0A0A;
  }

  .MuiDrawer-paper {
    background: #F5EDE0 !important;
    border-right: 1px solid rgba(255,255,255,0.55);
    box-shadow: 0 24px 64px -16px rgba(60,40,20,0.28);
    color: #0A0A0A;
  }

  .MuiOutlinedInput-root {
    background: rgba(255, 250, 240, 0.55);
    backdrop-filter: blur(14px) saturate(160%);
    -webkit-backdrop-filter: blur(14px) saturate(160%);
    border-radius: 16px !important;
    color: #0A0A0A;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255,255,255,0.55) !important;
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255,255,255,0.70) !important;
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #0A0A0A !important;
    border-width: 1px !important;
  }
  .MuiInputLabel-root { color: rgba(10,10,10,0.55); }
  .MuiInputLabel-root.Mui-focused { color: #0A0A0A; }
  .MuiInputBase-input { color: #0A0A0A; }
  .MuiInputBase-input::placeholder { color: rgba(10,10,10,0.40); opacity: 1; }
  .MuiFormHelperText-root { color: rgba(10,10,10,0.55); }
  .MuiSelect-select { color: #0A0A0A; }

  .MuiButton-contained {
    background: rgba(10,10,10,0.88) !important;
    color: #FFFAF0 !important;
    border-radius: 100px !important;
    box-shadow: none !important;
    font-weight: 500;
    letter-spacing: 0.08em;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .MuiButton-contained:hover {
    background: #0A0A0A !important;
    box-shadow: 0 8px 24px rgba(60,40,20,0.22) !important;
    transform: translateY(-1px);
  }
  .MuiButton-outlined {
    border-color: rgba(255,255,255,0.55) !important;
    color: #0A0A0A !important;
    background: rgba(255, 250, 240, 0.55) !important;
    border-radius: 100px !important;
    font-weight: 500;
  }
  .MuiButton-outlined:hover {
    background: rgba(255, 250, 240, 0.72) !important;
    border-color: rgba(255,255,255,0.70) !important;
  }

  .MuiIconButton-root { color: #0A0A0A; }
  .MuiIconButton-root svg { color: #0A0A0A; fill: #0A0A0A; }

  .MuiChip-root {
    background: rgba(255, 250, 240, 0.55) !important;
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border: 1px solid rgba(255,255,255,0.55) !important;
    border-radius: 100px !important;
    color: #0A0A0A !important;
  }
  .MuiChip-filled {
    background: rgba(10,10,10,0.88) !important;
    color: #FFFAF0 !important;
    border: none !important;
  }

  .MuiTypography-root { color: #0A0A0A; }

  .MuiDivider-root { border-color: rgba(10,10,10,0.08); }

  .MuiCheckbox-root { color: rgba(10,10,10,0.35); }
  .MuiCheckbox-root.Mui-checked { color: #0A0A0A; }
  .MuiRadio-root { color: rgba(10,10,10,0.35); }
  .MuiRadio-root.Mui-checked { color: #0A0A0A; }

  .MuiMenu-paper, .MuiPopover-paper {
    background: rgba(255, 250, 240, 0.92) !important;
    backdrop-filter: blur(28px) saturate(180%) !important;
    -webkit-backdrop-filter: blur(28px) saturate(180%) !important;
    border: 1px solid rgba(255,255,255,0.65) !important;
    border-radius: 20px !important;
    box-shadow: 0 16px 48px -12px rgba(60,40,20,0.22) !important;
  }
  .MuiMenuItem-root { color: #0A0A0A; border-radius: 10px; margin: 2px 6px; }
  .MuiMenuItem-root:hover { background: rgba(255, 250, 240, 0.72); }
  .MuiMenuItem-root.Mui-selected { background: rgba(255, 250, 240, 0.78); }

  .MuiBadge-badge {
    background: #0A0A0A !important;
    color: #FFFAF0 !important;
    font-size: 0.65rem;
    font-weight: 600;
  }

  .MuiDialog-paper {
    background: rgba(255, 250, 240, 0.78) !important;
    backdrop-filter: blur(36px) saturate(200%) !important;
    -webkit-backdrop-filter: blur(36px) saturate(200%) !important;
    border: 1px solid rgba(255,255,255,0.70) !important;
    border-radius: 32px !important;
    box-shadow: 0 24px 64px -16px rgba(60,40,20,0.28) !important;
  }

  .MuiSnackbar-root .MuiPaper-root {
    border-radius: 20px !important;
    backdrop-filter: blur(28px) saturate(180%) !important;
  }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(10,10,10,0.15); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(10,10,10,0.25); }
`;

export function ThemeWrapper({ children }: ThemeWrapperProps) {
  const theme = useTheme();
  const themeName = getConfiguredThemeName();
  const isLiquidGlass = themeName === ThemeName.LIQUID_GLASS;

  useEffect(() => {
    document.documentElement.style.backgroundColor = theme.colors.background.default;
    document.documentElement.style.color = theme.colors.text.primary;
    document.body.style.backgroundColor = theme.colors.background.default;
    document.body.style.color = theme.colors.text.primary;
    document.body.style.fontFamily = theme.typography.fontFamily.primary;

    const oldStyle = document.getElementById("theme-styles");
    if (oldStyle) oldStyle.remove();

    const style = document.createElement("style");
    style.id = "theme-styles";

    if (isLiquidGlass) {
      style.innerHTML = LIQUID_GLASS_GLOBAL_CSS;
    } else {
      style.innerHTML = `
        body, html {
          background-color: ${theme.colors.background.default};
          color: ${theme.colors.text.primary};
          font-family: ${theme.typography.fontFamily.primary};
        }
        *, *::before, *::after { font-family: ${theme.typography.fontFamily.primary} !important; }
      `;
    }

    document.head.appendChild(style);
    return () => {
      const s = document.getElementById("theme-styles");
      if (s) s.remove();
    };
  }, [theme, themeName, isLiquidGlass]);

  return <>{children}</>;
}
